import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { axiosErrorHandler } from 'common/helpers/axios.helper';
import { AppDispatch, RootState } from 'app/store';
import {
  ILegal,
  ICreateLegalPayload,
  IDeleteLegalPayload,
  ILegalListParams,
  ILegalParams,
  IUpdateBySellerLegalPayload,
  IUpdateLegalPayload,
} from 'entities/LegalEntity/LegalEntity.models';
import { legalEntityTransport } from 'entities/LegalEntity/LegalEntity.transport';

interface IState {
  legal: ILegal | null;
  legalList: ILegal[];
  legalCount: number;
  isLegalLoading: boolean;
  legalError: string | null;
}

const initialState: IState = {
  legal: null,
  legalList: [],
  legalCount: 0,
  isLegalLoading: false,
  legalError: null,
};

export const legalSlice = createSlice({
  name: 'legal',
  initialState,
  reducers: {
    setLegal(state, action: PayloadAction<ILegal | null>) {
      state.legal = action.payload;
    },
    setLegalList(state, action: PayloadAction<ILegal[]>) {
      state.legalList = action.payload;
    },
    setLegalCount(state, action: PayloadAction<number>) {
      state.legalCount = action.payload;
    },
    setIsLegalLoading(state, action: PayloadAction<boolean>) {
      state.isLegalLoading = action.payload;
    },
    setLegalError(state, action: PayloadAction<string | null>) {
      state.legalError = action.payload;
    },
    addLegalListItem(state, action: PayloadAction<ILegal>) {
      state.legalList.push(action.payload);
    },
    patchLegalListItem(state, action: PayloadAction<ILegal>) {
      const legal = state.legalList.find((item) => item.id === action.payload.id);

      if (!legal) {
        return;
      }

      Object.assign(legal, action.payload);
    },
    removeLegalListItem(state, action: PayloadAction<number>) {
      state.legalList = state.legalList.filter((item) => item.id !== action.payload);
    },
  },
});

export const {
  setLegal,
  setLegalList,
  setLegalCount,
  setIsLegalLoading,
  setLegalError,
  addLegalListItem,
  patchLegalListItem,
  removeLegalListItem,
} = legalSlice.actions;

export const selectLegal = (state: RootState) => state.legal.legal;
export const selectLegalList = (state: RootState) => state.legal.legalList;
export const selectLegalCount = (state: RootState) => state.legal.legalCount;
export const selectIsLegalLoading = (state: RootState) => state.legal.isLegalLoading;
export const selectLegalError = (state: RootState) => state.legal.legalError;

export const legalReducer = legalSlice.reducer;

export const getLegalList = (params: ILegalListParams) => async (dispatch: AppDispatch) => {
  dispatch(setIsLegalLoading(true));

  try {
    const response = await legalEntityTransport.get(params);
    dispatch(setLegalList(response.data));
    dispatch(setLegalCount(response.count));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsLegalLoading(false));
  }
};

export const getLegalById = (params: ILegalParams) => async (dispatch: AppDispatch) => {
  dispatch(setIsLegalLoading(true));

  try {
    const response = await legalEntityTransport.getById(params.id);
    dispatch(setLegal(response));
    params.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsLegalLoading(false));
  }
};

export const createLegal = (payload: ICreateLegalPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsLegalLoading(true));

  try {
    const response = await legalEntityTransport.create(payload);
    dispatch(addLegalListItem(response));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error, setLegalError);
  } finally {
    dispatch(setIsLegalLoading(false));
  }
};

export const updateLegal = (payload: IUpdateLegalPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsLegalLoading(true));

  try {
    const response = await legalEntityTransport.update(payload);
    dispatch(patchLegalListItem(response));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error, setLegalError);
  } finally {
    dispatch(setIsLegalLoading(false));
  }
};

export const deleteLegal = (payload: IDeleteLegalPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsLegalLoading(true));

  try {
    const response = await legalEntityTransport.delete(payload);
    dispatch(removeLegalListItem(response.id));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsLegalLoading(false));
  }
};

export const updateLegalBySeller = (payload: IUpdateBySellerLegalPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsLegalLoading(true));

  try {
    const response = await legalEntityTransport.updateBySeller(payload);
    dispatch(patchLegalListItem(response));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error, setLegalError);
  } finally {
    dispatch(setIsLegalLoading(false));
  }
};
